import { useState, useEffect } from 'react';
import { useAuth, uidToHue } from '../contexts/AuthContext';
import type { PublicProfile, MatchResult, Connection } from '../contexts/AuthContext';
import { getMatches } from '../lib/api';
import { useGlossary } from '../contexts/GlossaryContext';
import '../styles/UserDiscovery.css';

type Tab = 'matches' | 'invite' | 'contacts';
type TranslateFn = ReturnType<typeof useGlossary>['t'];

interface UserDiscoveryProps {
  onNavigateHome: () => void;
}

function joinList(items?: string[] | string, fallback = '—') {
  if (!items) return fallback;
  if (typeof items === 'string') return items || fallback;
  return items.length ? items.join(' · ') : fallback;
}

// ── Match card ──────────────────────────────────────────────────────────────

function MatchCard({ match, onAdd, t }: { match: MatchResult; onAdd: (m: MatchResult) => void; t: TranslateFn }) {
  const hue = uidToHue(match.uid);
  const initials = match.alias ? match.alias.slice(0, 2).toUpperCase() : '??';
  const scoreColor = match.score >= 70 ? '#27ae60' : match.score >= 40 ? '#f39c12' : '#e74c3c';
  const levelLabel = (value?: string) => value ? t(`profile.level.${value}`) : '—';
  const sessionLabel = (value?: string) => value ? t(`profile.session.${value}`) : '—';
  const lookingForLabel = (value?: string) => value ? t(`profile.lookingFor.${value}`) : '—';
  const profileLabel = match.profileKind === 'knowledge_provider'
    ? t('profile.kind.provider')
    : match.profileKind === 'both'
      ? t('profile.kind.both')
      : t('profile.kind.learner');
  return (
    <div className="ud-match-card">
      <div className="ud-match-avatar" style={{ background: `hsl(${hue},55%,45%)` }}>
        {initials}
      </div>
      <div className="ud-match-info">
        <div className="ud-match-alias">{match.alias || <em>{t('discovery.unknown')}</em>}</div>
        <div className="ud-match-badges">
          <span className="ud-match-badge">{profileLabel}</span>
          {match.pincode && <span className="ud-match-badge">📍 {match.pincode}</span>}
        </div>
        <div className="ud-match-meta">
          {levelLabel(match.learningLevel)} · {match.nativeLanguage || '—'} · {match.timeZone || '—'}
        </div>
        <div className="ud-match-meta">
          {t('discovery.match.subjects')}: {joinList(match.studyAreas)} · {t('discovery.match.qualification')}: {match.qualification || '—'}
        </div>
        <div className="ud-match-meta">
          {t('discovery.match.sessionModes')}: {joinList(match.sessionModes?.map(sessionLabel))} · {t('discovery.match.lookingFor')}: {lookingForLabel(match.lookingFor)}
        </div>
        <div className="ud-match-score-row">
          <div className="ud-score-bar-bg">
            <div className="ud-score-bar-fill" style={{ width: `${match.score}%`, background: scoreColor }} />
          </div>
          <span className="ud-score-label" style={{ color: scoreColor }}>{match.score}%</span>
        </div>
        <ul className="ud-reasons">
          {match.reasons.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      </div>
      <button className="ud-add-btn" onClick={() => onAdd(match)}>🤝 {t('discovery.button.add')}</button>
    </div>
  );
}

// ── Connection card ──────────────────────────────────────────────────────────

function ConnectionCard({ conn, onRemove, t }: { conn: Connection; onRemove: (uid: string) => void; t: TranslateFn }) {
  const hue = uidToHue(conn.uid);
  const initials = conn.alias ? conn.alias.slice(0, 2).toUpperCase() : '??';
  const levelLabel = (value?: string) => value ? t(`profile.level.${value}`) : '—';
  const sessionLabel = (value?: string) => value ? t(`profile.session.${value}`) : '—';
  const lookingForLabel = (value?: string) => value ? t(`profile.lookingFor.${value}`) : '—';
  const profileLabel = conn.profileKind === 'knowledge_provider'
    ? t('profile.kind.provider')
    : conn.profileKind === 'both'
      ? t('profile.kind.both')
      : t('profile.kind.learner');
  return (
    <div className="ud-conn-card">
      <div className="ud-conn-avatar" style={{ background: `hsl(${hue},55%,45%)` }}>{initials}</div>
      <div className="ud-conn-info">
        <div className="ud-conn-alias">{conn.alias || <em>{t('discovery.unknown')}</em>}</div>
        <div className="ud-match-badges">
          <span className="ud-match-badge">{profileLabel}</span>
          {conn.pincode && <span className="ud-match-badge">📍 {conn.pincode}</span>}
        </div>
        <div className="ud-conn-meta">{levelLabel(conn.learningLevel)} · {conn.nativeLanguage || '—'} · {lookingForLabel(conn.lookingFor)}</div>
        <div className="ud-conn-meta">
          {t('discovery.match.subjects')}: {joinList(conn.studyAreas)} · {t('discovery.match.qualification')}: {conn.qualification || '—'}
        </div>
        <div className="ud-conn-meta">
          {t('discovery.match.sessionModes')}: {joinList(conn.sessionModes?.map(sessionLabel))} · {t('discovery.match.timeZone')}: {conn.timeZone || '—'}
        </div>
        <div className="ud-conn-date">{t('discovery.connectedAt')}: {new Date(conn.connectedAt).toLocaleDateString('hi-IN')}</div>
      </div>
      <button className="ud-remove-btn" onClick={() => onRemove(conn.uid)} title={t('discovery.button.remove')}>✕</button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function UserDiscovery({ onNavigateHome }: UserDiscoveryProps) {
  const { t } = useGlossary();
  const {
    user, profile, connections,
    addConnection, removeConnection,
    redeemInvite, syncDiscovery,
  } = useAuth();

  const [tab, setTab] = useState<Tab>('matches');
  const [matches, setMatches] = useState<MatchResult[]>([]);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchError, setMatchError] = useState('');

  const [inviteInput, setInviteInput] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [invitePreview, setInvitePreview] = useState<PublicProfile | null>(null);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  // Load matches when the tab is opened
  useEffect(() => {
    if (tab !== 'matches' || !profile?.isDiscoverable) return;
    setMatchLoading(true);
    setMatchError('');
    getMatches(profile.uid)
      .then(setMatches)
        .catch(() => setMatchError(t('discovery.error.matches')))
      .finally(() => setMatchLoading(false));
  }, [tab, profile?.uid, profile?.isDiscoverable]);

  const handleLookupCode = async () => {
    const code = inviteInput.trim().toUpperCase();
    if (code.length !== 6) { setInviteError(t('discovery.error.codeLength')); return; }
    setInviteLoading(true);
    setInviteError('');
    setInvitePreview(null);
    try {
      const result = await redeemInvite(code);
      setInvitePreview(result.profile_snapshot);
    } catch (e: unknown) {
      const err = e as { status?: number };
        setInviteError(err.status === 410
          ? t('discovery.error.codeExpired')
          : t('discovery.error.codeInvalid')
        );
    } finally {
      setInviteLoading(false);
    }
  };

  const handleAddFromInvite = () => {
    if (!invitePreview) return;
    addConnection(invitePreview);
    setInvitePreview(null);
    setInviteInput('');
    setInviteSuccess(t('discovery.success.contactAdded'));
    setTimeout(() => setInviteSuccess(''), 3000);
  };

  const handleAddFromMatch = (m: MatchResult) => {
    addConnection(m);
    showToast(t('discovery.success.memberAdded', { name: m.alias || t('discovery.unknown') }));
  };

  if (!user || !profile) {
    return (
      <div className="ud-container">
        <p className="ud-empty">{t('discovery.empty.signIn')}</p>
        <button className="ud-btn-secondary" onClick={onNavigateHome}>{t('discovery.button.back')}</button>
      </div>
    );
  }

  return (
    <div className="ud-container">
      <div className="ud-header">
        <button className="ud-back-btn" onClick={onNavigateHome}>← {t('discovery.button.back')}</button>
        <h1 className="ud-heading">{t('discovery.title')}</h1>
      </div>

      {toast && <div className="ud-toast">{toast}</div>}

      {/* ── Tab bar ── */}
      <div className="ud-tabs">
          {([
            ['matches', t('discovery.tab.matches')],
            ['invite',  t('discovery.tab.invite')],
            ['contacts',t('discovery.tab.contacts')],
        ] as [Tab, string][]).map(([id, label]) => (
          <button
            key={id}
            className={`ud-tab ${tab === id ? 'ud-tab--active' : ''}`}
            onClick={() => setTab(id)}
          >
            {label}
            {id === 'contacts' && connections.length > 0 && (
              <span className="ud-badge">{connections.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab: Matches ── */}
      {tab === 'matches' && (
        <div className="ud-panel">
          {!profile.isDiscoverable ? (
            <div className="ud-discoverable-cta">
              <p>{t('discovery.cta.hidden')}</p>
              <button className="ud-btn-primary" onClick={async () => { await syncDiscovery(); showToast(t('discovery.success.enabled')); }}>
                {t('discovery.button.enable')}
              </button>
            </div>
          ) : matchLoading ? (
            <p className="ud-loading">{t('discovery.loading.matches')}</p>
          ) : matchError ? (
            <p className="ud-error">{matchError}</p>
          ) : matches.length === 0 ? (
                <p className="ud-empty">{t('discovery.empty.noMatches')}</p>
          ) : (
            <div className="ud-match-list">
              {matches.map(m => (
                <MatchCard key={m.uid} match={m} onAdd={handleAddFromMatch} t={t} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Invite Code ── */}
      {tab === 'invite' && (
        <div className="ud-panel">
            <p className="ud-panel-desc">{t('discovery.invite.description')}</p>
          <div className="ud-invite-input-row">
            <input
              className="ud-invite-input"
              value={inviteInput}
              onChange={e => { setInviteInput(e.target.value.toUpperCase().slice(0, 6)); setInviteError(''); }}
              placeholder={t('discovery.placeholder.code')}
              maxLength={6}
              spellCheck={false}
            />
              <button className="ud-btn-primary" onClick={handleLookupCode} disabled={inviteLoading}>
                {inviteLoading ? '⏳' : t('discovery.button.search')}
              </button>
          </div>
          {inviteError   && <p className="ud-error">{inviteError}</p>}
          {inviteSuccess && <p className="ud-success">{inviteSuccess}</p>}

          {invitePreview && (
            <div className="ud-invite-preview">
              <div className="ud-preview-title">{t('discovery.preview.title')}</div>
              <div className="ud-preview-avatar" style={{ background: `hsl(${uidToHue(invitePreview.uid)},55%,45%)` }}>
                {invitePreview.alias ? invitePreview.alias.slice(0, 2).toUpperCase() : '??'}
              </div>
              <div className="ud-preview-alias">{invitePreview.alias || <em>{t('discovery.unknown')}</em>}</div>
              <div className="ud-match-badges">
                <span className="ud-match-badge">
                  {invitePreview.profileKind === 'knowledge_provider'
                    ? t('profile.kind.provider')
                    : invitePreview.profileKind === 'both'
                      ? t('profile.kind.both')
                      : t('profile.kind.learner')}
                </span>
                {invitePreview.pincode && <span className="ud-match-badge">📍 {invitePreview.pincode}</span>}
              </div>
              <div className="ud-preview-meta">
                {t('discovery.preview.level')}: {t(`profile.level.${invitePreview.learningLevel}`)} ·
                {t('discovery.preview.language')}: {invitePreview.nativeLanguage || '—'} ·
                {t('discovery.preview.timeZone')}: {invitePreview.timeZone || '—'}
              </div>
              <div className="ud-preview-meta">
                {t('discovery.preview.subjects')}: {joinList(invitePreview.studyAreas)} · {t('discovery.preview.qualification')}: {invitePreview.qualification || '—'}
              </div>
              <div className="ud-preview-meta">
                {t('discovery.preview.sessionModes')}: {joinList(invitePreview.sessionModes?.map((mode) => t(`profile.session.${mode}`)))} · {t('discovery.preview.lookingFor')}: {invitePreview.lookingFor ? t(`profile.lookingFor.${invitePreview.lookingFor}`) : '—'}
              </div>
              <button className="ud-btn-primary" onClick={handleAddFromInvite}>{t('discovery.button.add')}</button>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Contacts ── */}
      {tab === 'contacts' && (
        <div className="ud-panel">
          {connections.length === 0 ? (
            <p className="ud-empty">{t('discovery.empty.noContacts')}</p>
          ) : (
            <div className="ud-conn-list">
              {connections.map(c => (
                <ConnectionCard key={c.uid} conn={c} onRemove={removeConnection} t={t} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
